import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  saveToGlobalState,
  removeSpecificData,
  getStoredData,
} from "../../utils/localStorage";

const API_URL = process.env.REACT_APP_API_URL;
const CONFIRMATION_TOKEN_KEY = "confirmation_token";
const COOLDOWN_KEY = "confirmacao_last_sent";
const COOLDOWN_SECONDS = 60;

const getRemainingTime = () => {
  const lastSent = localStorage.getItem(COOLDOWN_KEY);
  if (!lastSent) return 0;
  const diff = Math.floor((Date.now() - Number(lastSent)) / 1000);
  return Math.max(COOLDOWN_SECONDS - diff, 0);
};

export default function UseConfirmacaoEmail({ phoneConfirmation, onVerified }) {
  const navigate = useNavigate();
  const [code, setCode] = useState(["", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [timer, setTimer] = useState(() => getRemainingTime());

  const { setUser, setAccessToken } = useAuth();

  const dataStorage = getStoredData();

  useEffect(() => {
    if (timer <= 0) return;

    const interval = setInterval(() => {
      setTimer((prev) => {
        const next = prev - 1;
        if (next <= 0) localStorage.removeItem(COOLDOWN_KEY);
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  const getAuthHeaders = () => {
    const { [CONFIRMATION_TOKEN_KEY]: token } = getStoredData() || {};
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const generateCode = async () => {
    try {
      setError("");

      const res = await fetch(`${API_URL}/api/confirmacao/gerar/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          tipo_validacao: "email",
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data?.detail || "Erro ao gerar código");
        return;
      }

      localStorage.setItem(COOLDOWN_KEY, Date.now().toString());
      setTimer(COOLDOWN_SECONDS);
      setSuccess("Código reenviado com sucesso!");
      setCode(["", "", "", "", ""]);
    } catch {
      setError("Erro de conexão");
    }
  };

  const verifyCode = async () => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const fullCode = code.join("");

      const res = await fetch(`${API_URL}/api/confirmacao/validar/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          codigo: fullCode,
          tipo_validacao: "email",
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data?.detail || "Código inválido ou expirado");
        return false;
      }

      setSuccess("Email confirmado com sucesso!");

      if (phoneConfirmation == false) {
        if (data?.confirmation_token) {
          saveToGlobalState({
            [CONFIRMATION_TOKEN_KEY]: data.confirmation_token,
          });
        }

        if (onVerified) onVerified();
      } else {
        removeSpecificData(CONFIRMATION_TOKEN_KEY);

        console.log(data);

        if (data?.access) {
          setAccessToken(data.access);
        }

        if (data?.user) {
          setUser(data.user);
        }

        if (dataStorage?.tab_login == "CLIENTE") {
          navigate("/dashboard-client");
          return;
        } else if (dataStorage?.tab_login == "VIAJANTE") {
          navigate("/dashboard-traveler");
          return;
        } else if (dataStorage?.tab_login == "DESTINATÁRIO") {
          navigate("/dashboard-receiver");
          return;
        } else if (dataStorage?.tab_login == "MALA") {
          navigate("/qual-meio-transporte");
          return;
        } else if (dataStorage?.tab_login == "COMPRAR") {
          console.log(dataStorage);
          console.log(dataStorage?.payload);
          if (dataStorage?.payload) {
            const response = await fetch(
              `${API_URL}/api/create-trip-negotiations/`,
              {
                method: "POST",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                  ...(data.access
                    ? { Authorization: `Bearer ${data.access}` }
                    : {}),
                },
                body: JSON.stringify(dataStorage?.payload),
              },
            );
          }

          removeSpecificData("payload");
          navigate("/dashboard-client");
          return;
        } else if (dataStorage?.tab_login == "PERFIL") {
          window.location.replace("/my-profile");
          return;
        }

        navigate("/dashboard-client");
      }

      return true;
    } catch {
      setError("Erro de conexão");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (input, index) => {
    const value = input.value;
    if (isNaN(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setError("");

    if (value && index < 4) {
      document.querySelectorAll(".ev-input")[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      const inputs = document.querySelectorAll(".ev-input");
      inputs[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();

    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 5)
      .split("");

    const newCode = ["", "", "", "", ""];
    pasted.forEach((num, i) => {
      newCode[i] = num;
    });

    setCode(newCode);
  };

  return {
    code,
    timer,
    loading,
    error,
    success,
    handleChange,
    handleKeyDown,
    handlePaste,
    verifyCode,
    generateCode,
  };
}
